import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getConversations, ConversationSummary as ApiConversationSummary, deleteConversation, listProjectConversations, getAgent, moveConversationToProject } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { FolderIcon, MenuIcon } from './icons';
import MoveToProjectDialog from './MoveToProjectDialog';
import { notify } from './Notifications';
import { useProjects } from '../context/ProjectContext';
import SearchModal from './SearchModal';
import { SHORTCUTS, formatShortcut, useShortcut } from '../hooks/useKeyboardShortcuts';
import ProjectSidebar from './ProjectSidebar';

export interface ConversationSummary {
  id: string;
  title: string;
  lastMessageTime: string;
  isApplication?: boolean;
  applicationId?: string;
  agentId?: string;
  projectId?: string;
}

export interface ConversationSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onSelectConversation: (conversationId: string) => void;
  currentConversationId?: string;
  refreshTrigger?: number; // Used to trigger refresh from parent
  isLoadingConversation?: boolean;
  newConversation?: ConversationSummary; // New prop to directly receive a new conversation
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  isOpen,
  onToggle,
  onSelectConversation,
  currentConversationId,
  refreshTrigger,
  isLoadingConversation = false,
  newConversation
}) => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [movingConversation, setMovingConversation] = useState<ConversationSummary | null>(null);

  useShortcut(SHORTCUTS.search, () => setIsSearchModalOpen(true));
  useShortcut(SHORTCUTS.toggleSidebar, onToggle);
  useShortcut(SHORTCUTS.newChat, () => {
    if (!isLoadingConversation) onSelectConversation('');
  });
  const { getAccessToken } = useAuth();
  const { colors, theme } = useTheme();
  const { activeProjectId, selectProject } = useProjects();

  const moveConversation = async (conversation: ConversationSummary, projectId: string | null) => {
    setMovingConversation(null);
    try {
      await moveConversationToProject(conversation.id, projectId);
      // The open chat follows its project, as when it is opened, so the sidebar matches what the backend uses.
      if (conversation.id === currentConversationId && projectId !== activeProjectId) {
        await selectProject(projectId);
      } else {
        await loadConversations(true);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      notify(typeof detail === 'string' ? detail : 'Could not move the chat.');
    }
  };
  const history = useHistory();

  const loadConversations = async (force: boolean = false) => {
    if (!force && hasLoadedOnce) return; // Don't reload if already loaded unless forced

    setIsLoading(true);
    try {
      await getAccessToken();
      const apiConversations = activeProjectId
        ? await listProjectConversations(activeProjectId)
        : await getConversations();

      const transformedConversations: ConversationSummary[] = apiConversations.map(conv => ({
        id: conv.id,
        title: conv.title,
        lastMessageTime: formatRelativeTime(conv.last_message_time),
        isApplication: conv.is_application,
        applicationId: conv.application_id,
        agentId: conv.agent_id,
        projectId: conv.project_id,
      }));

      setConversations(transformedConversations);
      setHasLoadedOnce(true);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setConversations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatRelativeTime = (dateString: string): string => {
    try {
      if (!dateString) {
        return 'Unknown time';
      }
      
      // The API returns timestamps in UTC format but without 'Z' suffix
      // Add 'Z' to ensure proper UTC parsing
      const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
      const date = new Date(utcDateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString);
        return 'Invalid date';
      }
      
      const now = new Date();
      const diffInMilliseconds = now.getTime() - date.getTime();
      const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
      
      // Handle negative differences (future dates)
      if (diffInMinutes < 0) {
        return 'Just now';
      }
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
      
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting relative time:', error, 'for dateString:', dateString);
      return 'Unknown time';
    }
  };

  useEffect(() => {
    // Load conversations on component mount
    loadConversations();
  }, []);

  // Reload the conversation list whenever the active project changes (null = all).
  useEffect(() => {
    setHasLoadedOnce(false);
    loadConversations(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  // Expose refresh function through a prop
  useEffect(() => {
    if (isOpen && !hasLoadedOnce) {
      loadConversations();
    }
  }, [isOpen]);

  // Refresh when triggered by parent
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      // We don't need to reload all conversations if we have a new conversation prop
      if (!newConversation) {
        loadConversations(true);
      }
    }
  }, [refreshTrigger]);
  
  // Handle new conversation prop
  useEffect(() => {
    if (newConversation) {
      // Add the new conversation to the top of the list
      setConversations(prevConversations => {
        // Check if the conversation already exists to avoid duplicates
        const exists = prevConversations.some(conv => conv.id === newConversation.id);
        if (exists) {
          return prevConversations;
        }
        // Add at the beginning since it's the newest
        return [newConversation, ...prevConversations];
      });
      // Mark that we have loaded conversations at least once
      setHasLoadedOnce(true);
      
    }
  }, [newConversation]);

  const handleConversationClick = (conversationId: string) => {
    // Prevent selection if already loading
    if (isLoadingConversation) {
      return;
    }
    onSelectConversation(conversationId);
  };

  // Open the ServiceDesk agent directly — same navigation as picking it in Agents.
  const openServiceDeskAgent = async () => {
    try {
      const agent = await getAgent('zoho_servicedesk');
      if (agent.redirect_url) {
        window.open(agent.redirect_url, '_blank', 'noopener,noreferrer');

        return;
      }
      history.push('/', {
        agentData: {
          id: agent.id,
          title: agent.title,
          description: agent.description,
          img_url: agent.img_url,
          required_connection: agent.required_connection,
        },
      });
    } catch (err) {
      console.error('Error opening ServiceDesk agent:', err);
      history.push('/agents');
    }
  };

  const handleDeleteConversation = async (conversationId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent conversation selection when delete is clicked
    
    // If already deleting any conversation, don't do anything
    if (isDeleting) {
      return;
    }
    
    // Set deleting state to true
    setIsDeleting(true);
    
    try {
      await getAccessToken();
      await deleteConversation(conversationId);
      // Only remove from state after the deletion is successful
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
    } catch (error) {
      console.error('Error deleting conversation:', error);
    } finally {
      // Reset deleting state
      setIsDeleting(false);
    }
  };

  const truncateTitle = (title: string, maxLength: number = 40) => {
    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  };

  // Add CSS keyframes for spinner animation
  const spinnerAnimation = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;

  return (
    <>
      <style>{spinnerAnimation}</style>

      {/* Collapsed Sidebar - Always visible */}
      <div
        className="cv-rail"
        style={{
          position: 'fixed',
          top: '0',
          left: 0,
          height: '100vh',
          width: '60px',
          backgroundColor: colors.sidebarBg,
          boxShadow: theme === 'light' ? '2px 0 10px rgba(0, 0, 0, 0.1)' : '2px 0 10px rgba(0, 0, 0, 0.3)',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px 0',
          gap: '12px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {/* Toggle Button with Logo */}
        <button
          className="cv-lift"
          onClick={onToggle}
          style={{
            padding: '0',
            borderRadius: '12px',
            background: colors.surface,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
          }}
          title="Toggle sidebar"
          data-shortcut={formatShortcut(SHORTCUTS.toggleSidebar)}
          data-shortcut-pos="right"
        >
          <img
            src="/vnt-logo.png"
            alt="VNT Logo"
            className="logo-img"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              objectFit: 'cover',
              position: 'absolute',
              transition: 'opacity 0.2s ease',
            }}
          />
          <span
            className="toggle-icon"
            style={{
              display: 'flex',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '16px',
              color: colors.textPrimary,
              position: 'absolute',
              transition: 'opacity 0.2s ease',
            }}
          >
            <MenuIcon size={18} />
          </span>
        </button>

        {/* ServiceDesk shortcut (icon rail) */}
        <button
          className="cv-rail-btn"
          onClick={openServiceDeskAgent}
          style={{
            padding: '12px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            transition: 'all 0.2s ease',
            color: colors.textPrimary,
          }}
          title="ServiceDesk Assistant"
        >
          <img
            src="/assets/agent-icons/zoho-servicedesk.png"
            alt="ServiceDesk"
            style={{ width: '20px', height: '20px', objectFit: 'contain', borderRadius: '4px' }}
          />
        </button>

        {/* Agents Button */}
        <button
          className="cv-rail-btn"
          onClick={() => {
            history.push('/agents');
          }}
          style={{
            padding: '12px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            transition: 'all 0.2s ease',
            color: colors.textPrimary,
          }}
          title="Agents"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="8" r="5"/>
            <path d="M20 21a8 8 0 1 0-16 0"/>
          </svg>
        </button>

        {/* Applications Button */}
        <button
          className="cv-rail-btn"
          onClick={() => {
            history.push('/applications');
          }}
          style={{
            padding: '12px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            transition: 'all 0.2s ease',
            color: colors.textPrimary,
          }}
          title="Applications"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="7" height="7"/>
            <rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/>
          </svg>
        </button>

        {/* Search Button */}
        <button
          className="cv-rail-btn"
          onClick={() => setIsSearchModalOpen(true)}
          style={{
            padding: '12px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            transition: 'all 0.2s ease',
            color: colors.textPrimary,
          }}
          title="Search conversations"
          data-shortcut={formatShortcut(SHORTCUTS.search)}
          data-shortcut-pos="right"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
        </button>

        {/* New Chat Button */}
        <button
          className={`cv-rail-btn${isLoadingConversation ? " is-busy" : ""}`}
          onClick={() => {
            if (isLoadingConversation) {
              return;
            }
            onSelectConversation('');
          }}
          style={{
            padding: '12px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            cursor: isLoadingConversation ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            transition: 'all 0.2s ease',
            color: colors.textPrimary,
            opacity: isLoadingConversation ? 0.6 : 1,
          }}
          title="New chat"
          data-shortcut={formatShortcut(SHORTCUTS.newChat)}
          data-shortcut-pos="right"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4Z"/>
          </svg>
        </button>
      </div>

      {/* Full Sidebar */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: '0',
            left: 0,
            height: '100vh',
            width: '320px',
            backgroundColor: colors.sidebarBg,
            boxShadow: theme === 'light' ? '2px 0 10px rgba(0, 0, 0, 0.1)' : '2px 0 10px rgba(0, 0, 0, 0.3)',
            animation: 'slideIn 0.3s ease-in-out',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          <style>
            {`
              @keyframes slideIn {
                from {
                  transform: translateX(-320px);
                }
                to {
                  transform: translateX(0);
                }
              }
            `}
          </style>
        {/* Header */}
        <div
          style={{
            padding: '20px',
            paddingBottom: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Logo */}
          <img
            src="/vnt-logo.png"
            alt="VNT Logo"
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
          <button
            className="cv-lift"
            onClick={onToggle}
            style={{
              padding: '12px',
              borderRadius: '12px',
              background: colors.surface,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${colors.border}`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '16px',
              color: colors.textPrimary,
            }}
            title="Toggle sidebar"
            data-shortcut={formatShortcut(SHORTCUTS.toggleSidebar)}
            data-shortcut-pos="below"
          >
            <MenuIcon size={18} />
          </button>
        </div>

        {/* ServiceDesk shortcut — opens the zoho_servicedesk agent directly */}
        <div
          style={{
            padding: '16px 20px 8px 20px'
          }}
        >
          <button
            className="cv-panel-btn"
            style={{
              width: '100%',
              padding: '12px 16px',
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
            }}
            onClick={openServiceDeskAgent}
            title="ServiceDesk Assistant"
          >
            <img
              src="/assets/agent-icons/zoho-servicedesk.png"
              alt="ServiceDesk"
              style={{ width: '18px', height: '18px', objectFit: 'contain', borderRadius: '4px' }}
            />
            ServiceDesk Assistant
          </button>
        </div>

        {/* Agents Button */}
        <div
          style={{
            padding: '0 20px 8px 20px'
          }}
        >
          <button
            className="cv-panel-btn"
            style={{
              width: '100%',
              padding: '12px 16px',
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
            }}
            onClick={() => {
              history.push('/agents');
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="8" r="5"/>
              <path d="M20 21a8 8 0 1 0-16 0"/>
            </svg>
            Agents
          </button>
        </div>

        {/* Applications Button */}
        <div
          style={{
            padding: '0 20px 8px 20px'
          }}
        >
          <button
            className="cv-panel-btn"
            style={{
              width: '100%',
              padding: '12px 16px',
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
            }}
            onClick={() => {
              history.push('/applications');
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7"/>
              <rect x="14" y="3" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
            </svg>
            Applications
          </button>
        </div>

        {/* Search Conversations Button */}
        <div
          style={{
            padding: '0 20px 8px 20px'
          }}
        >
          <button
            className="cv-panel-btn"
            style={{
              width: '100%',
              padding: '12px 16px',
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
            }}
            onClick={() => setIsSearchModalOpen(true)}
            data-shortcut={formatShortcut(SHORTCUTS.search)}
            data-shortcut-pos="inside"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            Search conversations
          </button>
        </div>

        {/* New Chat Button */}
        <div
          style={{
            padding: '0 20px 16px 20px'
          }}
        >
          <button
            className={`cv-panel-btn${isLoadingConversation ? " is-busy" : ""}`}
            style={{
              width: '100%',
              padding: '12px 16px',
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoadingConversation ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              opacity: isLoadingConversation ? 0.6 : 1,
            }}
            onClick={() => {
              if (isLoadingConversation) {
                return;
              }
              onSelectConversation(''); // Empty string signals new conversation
            }}
            data-shortcut={formatShortcut(SHORTCUTS.newChat)}
            data-shortcut-pos="inside"
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="m18.5 2.5 3 3L12 15l-4 1 1-4Z"/>
            </svg>
            New chat
          </button>
        </div>

        {/* Projects Section */}
        <ProjectSidebar onProjectOpened={() => onSelectConversation('')} />

        {/* Recents header */}
        <div
          style={{
            padding: '10px 20px 4px',
            color: colors.textMuted,
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          Recents
        </div>

        {/* Conversations List */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '0',
          }}
        >
          {isLoading ? (
            <div
              style={{
                padding: '20px',
                textAlign: 'center',
                color: colors.textMuted,
              }}
            >
              Loading conversations...
            </div>
          ) : conversations.length === 0 ? (
            <div
              style={{
                padding: '20px',
                textAlign: 'center',
                color: colors.textMuted,
              }}
            >
              No conversations yet
            </div>
          ) : (
            conversations.map((conversation) => (
              <div
                className={`cv-conv-row${currentConversationId === conversation.id ? " is-selected" : ""}`}
                key={conversation.id}
                onClick={() => handleConversationClick(conversation.id)}
                style={{
                  padding: '16px 20px',
                  borderBottom: `1px solid ${colors.borderLight}`,
                  cursor: isLoadingConversation ? 'not-allowed' : 'pointer',
                  borderLeft: currentConversationId === conversation.id ? `3px solid ${colors.primary}` : '3px solid transparent',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  opacity: isLoadingConversation ? 0.6 : 1,
                  pointerEvents: isLoadingConversation ? 'none' : 'auto',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: colors.textPrimary,
                      marginBottom: '4px',
                      lineHeight: '1.3',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {conversation.agentId && (
                      <span title="Agent chat" style={{ display: 'flex', flexShrink: 0 }}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ color: colors.primary }}
                        >
                          <circle cx="12" cy="8" r="5"/>
                          <path d="M20 21a8 8 0 1 0-16 0"/>
                        </svg>
                      </span>
                    )}
                    {conversation.isApplication && conversation.applicationId && (
                      <span title="Application chat" style={{ display: 'flex', flexShrink: 0 }}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ color: colors.primary }}
                        >
                          <rect x="3" y="3" width="7" height="7"/>
                          <rect x="14" y="3" width="7" height="7"/>
                          <rect x="14" y="14" width="7" height="7"/>
                          <rect x="3" y="14" width="7" height="7"/>
                        </svg>
                      </span>
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {truncateTitle(conversation.title)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: colors.textMuted,
                    }}
                  >
                    {conversation.lastMessageTime}
                  </div>
                </div>
                {!conversation.agentId && !conversation.isApplication && (
                  <button
                    type="button"
                    className="cv-conv-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMovingConversation(conversation);
                    }}
                    title="Move to project"
                    aria-label={`Move ${conversation.title} to a project`}
                  >
                    <FolderIcon size={16} />
                  </button>
                )}
                <button
                  className="cv-conv-delete"
                  onClick={(e) => handleDeleteConversation(conversation.id, e)}
                  style={{
                    border: 'none',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    padding: '6px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    marginLeft: '8px',
                    flexShrink: 0,
                    opacity: isDeleting ? 0.5 : 1,
                  }}
                  title="Delete conversation"
                >
                  {isDeleting ? (
                    <span style={{ display: 'inline-block', width: '16px', height: '16px', borderTop: '2px solid currentColor', borderRight: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }}></span>
                  ) : (
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M3 6h18"/>
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                    </svg>
                  )}
                </button>
              </div>
            ))
          )}
        </div>

      </div>
      )}

      {/* Search Modal */}
      {movingConversation && (
        <MoveToProjectDialog
          title={movingConversation.title}
          currentProjectId={movingConversation.projectId ?? null}
          onChoose={(projectId) => moveConversation(movingConversation, projectId)}
          onClose={() => setMovingConversation(null)}
        />
      )}

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSelectConversation={onSelectConversation}
      />
    </>
  );
};

export default ConversationSidebar;